import type { CyclePhase, CyclePrediction } from '../../utils/cyclePrediction';
import { getPhaseColor } from '../../utils/cyclePrediction';

interface Props {
  /** The full cycle prediction — single source of truth for the ring. */
  prediction: CyclePrediction;
  /** Override label for "Period" (e.g., "Withdrawal bleed", "Bleeding") */
  bleedingLabel?: string;
  className?: string;
  hideText?: boolean;
}

const CX = 100;
const CY = 100;
const RADIUS = 80;
const STROKE_WIDTH = 16;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const PHASES: CyclePhase[] = ['menstrual', 'follicular', 'ovulatory', 'luteal'];

export default function PhaseRing({
  prediction,
  bleedingLabel = 'Period',
  className,
  hideText,
}: Props) {
  const {
    currentCycleDay,
    currentPhase,
    averageCycleLength,
    averagePeriodLength,
    daysUntilNextPeriod,
    fertileWindowStart,
    fertileWindowEnd,
    estimatedOvulationDay,
    isFertileToday,
    isOvulationToday,
  } = prediction;
  const totalDays = averageCycleLength;

  // Physiologically accurate: luteal ~14 days fixed, ovulatory ~2 days, follicular is variable
  const menstrualDays = averagePeriodLength ?? 5;
  const lutealDays = 14;
  const ovulatoryDays = 2;
  const follicularDays = totalDays - menstrualDays - ovulatoryDays - lutealDays;

  const phaseDayMap: Record<string, number> = {
    menstrual: menstrualDays,
    follicular: follicularDays,
    ovulatory: ovulatoryDays,
    luteal: lutealDays,
  };

  let cumulativeFraction = 0;
  const arcs = PHASES.map((phase) => {
    const days = phaseDayMap[phase];
    const fraction = days / totalDays;
    const offset = cumulativeFraction;
    cumulativeFraction += fraction;
    return { phase, fraction, offset };
  });

  // currentCycleDay is normalized to [1, averageCycleLength] by the prediction
  // engine (it rolls projected cycles forward), so the marker maps directly.
  const displayDay = Math.min(currentCycleDay, totalDays);
  const dayFraction = (displayDay - 0.5) / totalDays;
  const angle = dayFraction * 2 * Math.PI - Math.PI / 2;
  const markerX = CX + RADIUS * Math.cos(angle);
  const markerY = CY + RADIUS * Math.sin(angle);

  // Fertile window inner arc — bigger when rendered small (hideText)
  const INNER_RADIUS = hideText ? 60 : 66;
  const INNER_CIRC = 2 * Math.PI * INNER_RADIUS;
  let fertileArc: { fraction: number; offset: number } | null = null;
  if (fertileWindowStart != null && fertileWindowEnd != null) {
    const startFraction = (fertileWindowStart - 1) / totalDays;
    const endFraction = fertileWindowEnd / totalDays;
    fertileArc = {
      fraction: endFraction - startFraction,
      offset: startFraction,
    };
  }

  // Ovulation diamond marker position
  let ovulationMarker: { x: number; y: number } | null = null;
  if (estimatedOvulationDay != null) {
    const ovAngle = ((estimatedOvulationDay - 0.5) / totalDays) * 2 * Math.PI - Math.PI / 2;
    ovulationMarker = {
      x: CX + RADIUS * Math.cos(ovAngle),
      y: CY + RADIUS * Math.sin(ovAngle),
    };
  }

  // Center text — context-aware
  let topLine: string;
  let mainLine: string;
  const fertile = isFertileToday || isOvulationToday;
  if (isOvulationToday) {
    topLine = 'Ovulation';
    mainLine = 'today';
  } else if (isFertileToday) {
    topLine = 'Fertile';
    mainLine = 'window';
  } else if (currentPhase === 'menstrual') {
    topLine = bleedingLabel;
    mainLine = `day ${currentCycleDay}`;
  } else if (daysUntilNextPeriod > 1) {
    topLine = `${bleedingLabel} in`;
    mainLine = `${daysUntilNextPeriod} days`;
  } else {
    // Engine guarantees daysUntilNextPeriod >= 1 (cycles roll forward, never
    // overdue), so the remaining case is "tomorrow".
    topLine = bleedingLabel;
    mainLine = 'tomorrow';
  }

  return (
    <svg viewBox="0 0 200 200" className={className ?? "w-40 h-40 sm:w-48 sm:h-48 md:w-52 md:h-52"}>
      {arcs.map(({ phase, fraction, offset }) => (
        <circle
          key={phase}
          cx={CX}
          cy={CY}
          r={RADIUS}
          fill="none"
          stroke={getPhaseColor(phase)}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={`${fraction * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          strokeDashoffset={-offset * CIRCUMFERENCE}
          strokeLinecap="butt"
          transform={`rotate(-90 ${CX} ${CY})`}
          className="transition-all duration-300"
        />
      ))}

      {/* Fertile window inner arc */}
      {fertileArc && (
        <circle
          cx={CX}
          cy={CY}
          r={INNER_RADIUS}
          fill="none"
          stroke="#38BDF8"
          strokeWidth={hideText ? 14 : 8}
          strokeDasharray={`${fertileArc.fraction * INNER_CIRC} ${INNER_CIRC}`}
          strokeDashoffset={-fertileArc.offset * INNER_CIRC}
          strokeLinecap="round"
          transform={`rotate(-90 ${CX} ${CY})`}
          opacity={hideText ? 0.85 : 0.7}
          className="transition-all duration-300"
        />
      )}

      {/* Ovulation diamond on outer ring (rendered below today marker if not today) */}
      {ovulationMarker && !isOvulationToday && (
        <rect
          x={ovulationMarker.x - (hideText ? 6 : 4)}
          y={ovulationMarker.y - (hideText ? 6 : 4)}
          width={hideText ? 12 : 8}
          height={hideText ? 12 : 8}
          rx={1}
          fill="#047857"
          transform={`rotate(45 ${ovulationMarker.x} ${ovulationMarker.y})`}
        />
      )}

      {/* Current day ("today") marker — deliberately prominent: a pulsing glow
          halo + enlarged white disc with a bold colored ring, so it reads as
          "you are here" against any phase segment. */}
      {(() => {
        const markerColor = isOvulationToday ? '#047857' : getPhaseColor(currentPhase);
        const haloR = hideText ? 18 : 15;
        const discR = hideText ? 13 : 9;
        const innerR = hideText ? 6 : 4.5;
        return (
          <>
            {/* Pulsing glow draws the eye to today */}
            <circle
              cx={markerX}
              cy={markerY}
              r={haloR}
              fill="#ffffff"
              fillOpacity={0.55}
              className="animate-pulse"
            />
            {/* Always-white disc (bright in dark mode too) with a bold colored ring */}
            <circle
              cx={markerX}
              cy={markerY}
              r={discR}
              fill="#ffffff"
              stroke={markerColor}
              strokeWidth={hideText ? 4 : 3}
            />
            {/* Center: emerald diamond on ovulation day, phase-colored dot otherwise */}
            {isOvulationToday ? (
              <rect
                x={markerX - innerR}
                y={markerY - innerR}
                width={innerR * 2}
                height={innerR * 2}
                rx={1}
                fill="#047857"
                transform={`rotate(45 ${markerX} ${markerY})`}
              />
            ) : (
              <circle cx={markerX} cy={markerY} r={innerR} fill={markerColor} />
            )}
          </>
        );
      })()}

      {!hideText && (
        <>
          <text x={CX} y={CY - 14} textAnchor="middle" fontSize="13" fontWeight="500" className={fertile ? 'fill-sky-600 dark:fill-sky-400' : 'fill-gray-700 dark:fill-gray-300'}>
            {topLine}
          </text>
          <text x={CX} y={CY + 8} textAnchor="middle" fontSize="24" fontWeight="700" className={fertile ? 'fill-sky-700 dark:fill-sky-300' : 'fill-gray-800 dark:fill-gray-100'}>
            {mainLine}
          </text>
        </>
      )}
    </svg>
  );
}
